import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Storefront,
  MagnifyingGlass,
  Star,
  Package,
  CheckCircle,
  Lightning,
  Funnel,
  X,
  Link as PhosphorLink,
} from '@phosphor-icons/react';
import { marketplaceApi, MarketplaceSupplier } from '@dukene/shared/api/marketplace';
import { useAuthStore, useNotificationStore } from '@dukene/shared';
import { SuppliersSubNav } from './Suppliers';

const BUSINESS_TYPES = [
  { value: 'MANUFACTURER', label: 'Manufacturer' },
  { value: 'WHOLESALER', label: 'Wholesaler' },
  { value: 'DISTRIBUTOR', label: 'Distributor' },
  { value: 'DROPSHIPPER', label: 'Dropshipper' },
];

const INTEGRATION_TYPES = [
  { value: 'CATALOG', label: 'Catalog (Manual)' },
  { value: 'API_OAUTH', label: 'API Integration' },
];

export default function Marketplace() {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const [suppliers, setSuppliers] = useState<MarketplaceSupplier[]>([]);
  const [connectedSupplierIds, setConnectedSupplierIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    businessType: '',
    integrationType: '',
    minRating: 0,
    category: '',
  });

  useEffect(() => {
    loadSuppliers();
  }, [filters, searchQuery]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const [suppliersData, connectedData] = await Promise.all([
        marketplaceApi.getSuppliers({
          search: searchQuery || undefined,
          businessType: filters.businessType || undefined,
          integrationType: filters.integrationType || undefined,
          minRating: filters.minRating || undefined,
        }),
        user?.storeId
          ? marketplaceApi.getConnectedSuppliers(user.storeId)
          : Promise.resolve([]),
      ]);

      setSuppliers(suppliersData?.suppliers || []);

      const connections = Array.isArray(connectedData)
        ? connectedData
        : (connectedData as any)?.connections || [];
      setConnectedSupplierIds(new Set(connections.map((c: any) => c.supplierId)));
    } catch (error: any) {
      useNotificationStore.getState().addNotification({
        type: 'error',
        message: error.message || 'Failed to load suppliers',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (supplier: MarketplaceSupplier) => {
    const { addNotification } = useNotificationStore.getState();
    if (!user?.storeId) {
      addNotification({ type: 'error', message: 'Store identification missing' });
      return;
    }

    try {
      const result = await marketplaceApi.connectSupplier(user.storeId, {
        supplierId: supplier.id,
        autoSync: true,
      });

      if (result.requiresOAuth && result.authorizationUrl) {
        const authUrl = new URL(result.authorizationUrl);
        if (token) {
          authUrl.searchParams.set('token', token);
        }

        addNotification({
          type: 'success',
          message: 'Redirecting to authorization gateway...',
        });
        window.location.href = authUrl.toString();
      } else {
        addNotification({ type: 'success', message: `Connected to ${supplier.name}!` });
        navigate(`/suppliers/marketplace/${supplier.id}`);
      }
    } catch (error: any) {
      addNotification({
        type: 'error',
        message: error.message || 'Failed to connect supplier partner',
      });
    }
  };

  const clearFilters = () => {
    setFilters({
      businessType: '',
      integrationType: '',
      minRating: 0,
      category: '',
    });
  };

  return (
    <div className="space-y-5">
      <SuppliersSubNav activeTab="MARKETPLACE" />

      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-[#202223] dark:text-white">
            B2B Dropship & Wholesale Marketplace
          </h1>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
            Connect with verified suppliers to automatically import catalogs and fulfill orders
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className={`h-8 px-3 rounded-lg border text-xs font-semibold inline-flex items-center gap-1.5 transition-colors shrink-0 ${
            showFilters
              ? 'bg-[#E3F1DF] text-[#006838] border-[#B3DEC1]'
              : 'border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#202223] dark:text-white hover:bg-[#F6F6F7]'
          }`}
        >
          <Funnel weight="bold" className="w-3.5 h-3.5" />
          {showFilters ? 'Hide Filter Controls' : 'Filter Suppliers'}
        </button>
      </div>

      {/* Search Bar & Optional Filter Panel */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm space-y-3">
        <div className="relative">
          <MagnifyingGlass
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
          />
          <input
            type="text"
            placeholder="Search B2B supplier partners by name, category, or keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
          />
        </div>

        {showFilters && (
          <div className="pt-3 border-t border-[#E1E4E8] dark:border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                Business Model
              </label>
              <select
                value={filters.businessType}
                onChange={(e) => setFilters({ ...filters, businessType: e.target.value })}
                className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
              >
                <option value="">All Business Types</option>
                {BUSINESS_TYPES.map((bt) => (
                  <option key={bt.value} value={bt.value}>
                    {bt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] mb-1">
                Integration Type
              </label>
              <select
                value={filters.integrationType}
                onChange={(e) => setFilters({ ...filters, integrationType: e.target.value })}
                className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white"
              >
                <option value="">All Integration Modes</option>
                {INTEGRATION_TYPES.map((it) => (
                  <option key={it.value} value={it.value}>
                    {it.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={clearFilters}
                className="h-8 px-3 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] text-[#5C5F62] text-xs font-semibold inline-flex items-center justify-center gap-1"
              >
                <X weight="bold" className="w-3.5 h-3.5" />
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Supplier Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-44 bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 rounded-xl"
            />
          ))}
        </div>
      ) : suppliers.length === 0 ? (
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-14 text-center">
          <Storefront weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
            No Verified Suppliers Found
          </h3>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 max-w-sm mx-auto">
            Try adjusting your search terms or clearing your filters to browse the entire marketplace directory.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((supplier) => {
            const isConnected = connectedSupplierIds.has(supplier.id);

            return (
              <div
                key={supplier.id}
                className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col justify-between hover:border-[#006838] transition-colors"
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#E3F1DF] text-[#006838] flex items-center justify-center font-bold text-sm shrink-0">
                        {supplier.name?.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#202223] dark:text-white text-sm">
                          {supplier.name}
                        </h3>
                        {supplier.categories?.[0] && (
                          <span className="text-[11px] text-[#5C5F62] dark:text-slate-400">
                            {supplier.categories[0]}
                          </span>
                        )}
                      </div>
                    </div>

                    {supplier.isVerified && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#EBF5FA] text-[#006FBB]">
                        <CheckCircle weight="fill" className="w-3 h-3" />
                        Verified
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-3 line-clamp-2">
                    {supplier.description ||
                      'Automated wholesale and dropshipping distributor with full catalog sync support.'}
                  </p>

                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#E1E4E8] dark:border-slate-800 text-xs">
                    <div className="flex items-center gap-1 text-[#202223] dark:text-slate-200">
                      <Star weight="fill" className="w-3.5 h-3.5 text-amber-500" />
                      <span className="font-bold">{supplier.rating || '4.8'}</span>
                    </div>

                    <div className="flex items-center gap-1 text-[#5C5F62]">
                      <Package weight="bold" className="w-3.5 h-3.5" />
                      <span>{supplier.productCount || 120}+ items</span>
                    </div>

                    {supplier.requiresOAuth && (
                      <span className="inline-flex items-center gap-1 text-[#006838] font-semibold text-[11px]">
                        <Lightning weight="fill" className="w-3 h-3" />
                        Live API
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-[#E1E4E8] dark:border-slate-800 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/suppliers/marketplace/${supplier.id}`)}
                    className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-xs font-semibold text-[#202223] dark:text-white transition-colors"
                  >
                    Details
                  </button>

                  {isConnected ? (
                    <span className="h-8 px-3 rounded-lg bg-[#E3F1DF] text-[#006838] text-xs font-bold inline-flex items-center gap-1.5">
                      <CheckCircle weight="fill" className="w-3.5 h-3.5" />
                      Connected
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleConnect(supplier)}
                      className="h-8 px-3.5 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold inline-flex items-center gap-1.5 shadow-xs transition-colors"
                    >
                      <PhosphorLink weight="bold" className="w-3.5 h-3.5" />
                      Connect Supplier
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
